import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Eye } from "react-feather";
import MasterDetailListShell from "../../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../../components/layout/MasterDetailRow";
import SalespersonPicker from "../../components/SalespersonPicker";
import { useListOrdersQuery } from "../../salesApi";
import { useCanCreateConfirmedOrder, useCanViewAllSales } from "../../useSalesPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { orderStatus } from "../../salesStatusMaps";
import { paths } from "../../../../routes/paths";

// Exact uppercase/underscore tokens the API's sp_app_sales_order_list
// expects. Batch162: ALL is now every order - open, closed and cancelled - so
// every confirmed enquiry's order is in one place (it used to be open-only,
// which hid closed orders from "All"). OPEN is the old open-only list.
const VIEW_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "OPEN", label: "Open" },
  { value: "ONGOING", label: "On Going" },
  { value: "ONHOLD", label: "On Hold" },
  { value: "GUARDS_TO_DEPLOY", label: "Guards To Deploy" },
  { value: "COMMISSION_NOT_PAID", label: "Commission Not Paid" },
  { value: "CLOSED", label: "Closed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "ALARMS", label: "Alarms" },
  { value: "SCHEDULED", label: "Scheduled" },
];

const money = (n) => Number(n || 0).toFixed(2);

const OrderCompactList = () => {
  const canCreateConfirmed = useCanCreateConfirmedOrder();
  // See EnquiryCompactList: pointless without Sales.ViewAll, since the list is
  // then already scoped to your own records anyway.
  const canViewAll = useCanViewAllSales();
  const navigate = useNavigate();
  const { recordGuid: selectedRecordGuid } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("ALL");
  const [salesperson, setSalesperson] = useState(null);
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Same fix as EnquiryCompactList: the box promised "customer or order
  // number" but only sent customerName. @OrderNumber is an exact match
  // server-side, so a bare or partial number is padded to the SO-000000 shape.
  const looksLikeOrderNumber = /^(so-?)?\d{1,6}$/i.test(search);
  const orderNumberTerm = looksLikeOrderNumber
    ? `SO-${search.replace(/^so-?/i, "").padStart(6, "0")}`
    : undefined;

  const { data, isLoading } = useListOrdersQuery({
    branchId: selectedBranchId ?? undefined,
    view,
    customerName: orderNumberTerm ? undefined : search || undefined,
    orderNumber: orderNumberTerm,
    salespersonEmployeeRecordGuid: salesperson?.value || undefined,
    pageSize: 200,
  });

  const items = data?.items || [];

  return (
    <MasterDetailListShell
      title="Sales Orders"
      addButton={
        canCreateConfirmed && (
          <button type="button" className="btn btn-added" onClick={() => navigate(paths.salesOrderNewConfirmed)}>
            <PlusCircle className="me-2" size={16} />
            Confirmed Order
          </button>
        )
      }
      filters={
        <>
          <input
            type="text"
            placeholder="Search customer or order number"
            className="form-control form-control-sm"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <div className="d-flex gap-2 mt-2">
            <select className="form-control form-control-sm" value={view} onChange={(e) => setView(e.target.value)}>
              {VIEW_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {/* Find every order belonging to one salesperson, by employee code or
              name (Batch153) - the salesperson is an employee, not a login. */}
          {canViewAll && (
            <div className="mt-2">
              <label className="form-label text-muted mb-1" style={{ fontSize: 11 }}>
                Salesperson (employee)
              </label>
              <SalespersonPicker value={salesperson} onChange={setSalesperson} />
            </div>
          )}
        </>
      }
      isLoading={isLoading}
      emptyLabel="No orders match these filters."
      items={items}
      getKey={(o) => o.recordGuid}
      resetKey={`${search}|${view}|${salesperson?.value || ""}|${selectedBranchId}`}
      renderItem={(o) => {
        const isSelected = String(o.recordGuid) === String(selectedRecordGuid);
        return (
          <MasterDetailRow
            isSelected={isSelected}
            onClick={() => navigate(paths.salesOrderDetail(o.recordGuid))}
            avatarLabel={(o.customerName || "?").trim().slice(0, 2).toUpperCase()}
            // Customer leads, order number is the detail line (2026-09-12).
            title={<>{o.customerName || o.orderNumber} <span className="text-muted ms-1">{money(o.totalAmount)}</span></>}
            subtitle={<>{o.orderNumber} &middot; {o.branchCode}{o.salespersonName ? <> &middot; {o.salespersonName}</> : null}</>}
            badge={
              <span className={`badge ${orderStatus(o).cls} me-2`}>{orderStatus(o).label}</span>
            }
            actions={
              <button
                type="button"
                className="btn btn-link p-1"
                title="View"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(paths.salesOrderDetail(o.recordGuid));
                }}
              >
                <Eye size={15} />
              </button>
            }
          />
        );
      }}
    />
  );
};

export default OrderCompactList;
